import { useEffect, useState } from "react";
import SidebarComponent from "../components/common/SidebarComponent";
import { useMutation } from "@tanstack/react-query";
import { CircularProgress, InputAdornment, TextField } from "@mui/material";
import { IoCalendarNumberOutline } from "react-icons/io5";
import moment from "moment";
import { LuSearch } from "react-icons/lu";
import ModalDateRange from "../components/modalComp/DateRangeModal";
import { getListHistoryDoctor } from "../services/telemedicineApi";
import { CgCloseO, CgCheckO } from "react-icons/cg";
import PaginationComponent from "../components/common/PaginationComponent";
import { useNavigate } from "react-router-dom";

const HistoryDoctorPage = () => {
  const navigate = useNavigate();
  const isDoctorActive = localStorage.getItem("is_doctor_active") === "true";

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(1);
  const [selectedDates, setSelectedDates] = useState(["", ""]);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [reqBody, setReqBody] = useState({
    limit,
    offset,
    search: "",
    start_date: "",
    end_date: "",
  });

  const getDisplayDateRange = () => {
    if (selectedDates[0] && selectedDates[1]) {
      const startDate = moment(selectedDates[0]).format("DD MMM YYYY");
      const endDate = moment(selectedDates[1]).format("DD MMM YYYY");
      return `${startDate} - ${endDate}`;
    }
    return "Pilih Tanggal Kustom";
  };

  const {
    mutate: mutateListHistory,
    data,
    isPending,
  } = useMutation({
    mutationFn: getListHistoryDoctor,
  });

  useEffect(() => {
    setReqBody({
      ...reqBody,
      limit,
      offset,
      start_date: selectedDates[0],
      end_date: selectedDates[1],
    });
  }, [limit, offset, selectedDates]);

  useEffect(() => {
    mutateListHistory(reqBody);
  }, [reqBody]);

  return (
    <div className="flex bg-indigo-50">
      <div className="w-0 lg:w-[96px] h-[100vh] sticky top-0 z-30">
        <SidebarComponent />
      </div>

      <div className="w-[100%] lg:w-[calc(100%-96px)]">
        <div className="h-[calc(100vh-10px)] flex flex-col overflow-hidden p-6">
          <div className="max-w-7xl mx-auto flex flex-col min-h-0 w-full">
            <h1 className="text-3xl font-bold text-slate-700 mb-6 flex-shrink-0">
              Histori
            </h1>
            <div className="bg-white rounded-2xl p-6 mb-10 flex flex-col min-h-0">
              <div className="flex w-full items-end gap-4 mb-3">
                <div className="w-1/2">
                  <p className="font-medium text-slate-700 text-base mb-1">
                    Cari nama pasien / pemilik akun / ID transaksi
                  </p>

                  <TextField
                    fullWidth
                    value={reqBody.search}
                    onChange={(e) =>
                      setReqBody({ ...reqBody, search: e.target.value })
                    }
                    placeholder="Search"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LuSearch size={18} className="text-gray-400" />
                        </InputAdornment>
                      ),
                      sx: {
                        fontFamily: '"Montserrat", sans-serif',
                        height: "40px",
                        borderRadius: "10px",
                        fontSize: "14px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e2e8f0",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6366f1",
                        },
                      },
                    }}
                    sx={{
                      backgroundColor: "#fff",
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        borderRadius: "10px",
                      },
                    }}
                  />
                </div>

                <div className="flex items-end justify-end gap-3 w-1/2">
                  <div className="w-2/3">
                    <p className="font-medium text-slate-700 text-base mb-1">
                      Period
                    </p>

                    <TextField
                      fullWidth
                      value={getDisplayDateRange()}
                      onClick={() => setOpenCalendar(true)}
                      readOnly
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IoCalendarNumberOutline
                              size={18}
                              className="text-gray-400"
                            />
                          </InputAdornment>
                        ),
                        sx: {
                          fontFamily: '"Montserrat", sans-serif',
                          height: "40px",
                          borderRadius: "10px",
                          fontSize: "14px",
                          cursor: "pointer",
                          "& .MuiInputBase-input": {
                            fontFamily: '"Montserrat", sans-serif',
                            cursor: "pointer",
                            color:
                              selectedDates[0] && selectedDates[1]
                                ? "inherit"
                                : "#9ca3af",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e2e8f0",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6366f1",
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          borderRadius: "10px",
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="border rounded-xl text-md flex flex-col min-h-0 overflow-hidden">
                <div className="overflow-x-auto flex-1 min-h-0">
                  <table className="w-full">
                    <thead className="bg-blue-100 text-base text-slate-700 sticky top-0 z-10">
                      <tr>
                        <th className="py-3 px-4 font-semibold text-left truncate">
                          Waktu
                        </th>
                        <th className="py-3 px-4 font-semibold text-left truncate">
                          Pasien
                        </th>
                        <th className="py-3 px-4 font-semibold text-left truncate">
                          Pemilik Akun
                        </th>
                        <th className="py-3 px-4 font-semibold text-left truncate">
                          Diagnosa
                        </th>
                        <th className="py-3 px-4 font-semibold text-center truncate">
                          Resep
                        </th>
                        {isDoctorActive && (
                          <th className="py-3 px-4 font-semibold text-center truncate">
                            Aksi
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {data && data.consultations?.length > 0 && !isPending ? (
                        data.consultations.map((item, index) => (
                          <tr
                            key={index}
                            className="border-b last:border-b-0 border-slate-200 bg-white hover:bg-blue-50"
                          >
                            <td className="text-left truncate px-4 align-center py-3">
                              {item.consultation_date}
                            </td>
                            <td className="text-left px-4 py-3 align-center truncate text-primary font-medium">
                              {item.patient_name}
                            </td>
                            <td className="text-left truncate px-4 align-center py-3">
                              {item.patient_type}
                            </td>
                            <td className="text-left truncate px-4 align-center py-3">
                              {item.diagnosis === "" ? "-" : item.diagnosis}
                            </td>
                            <td className="text-left truncate px-4 align-center py-3 flex items-center justify-center">
                              {item.recipes.length > 0 ? (
                                <CgCloseO size={20} className="text-error" />
                              ) : (
                                <CgCheckO
                                  size={20}
                                  className="text-secondary"
                                />
                              )}
                            </td>
                            {isDoctorActive && (
                              <td className="text-center truncate px-4 align-center">
                                <button
                                  onClick={() =>
                                    navigate(`/consultations/${item.topic_id}`)
                                  }
                                  className="border rounded-lg px-3 py-1 bg-white text-slate-700 font-medium cursor-pointer transition-all duration-300 hover:bg-slate-50"
                                >
                                  Detail Konsultasi
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : isPending ? (
                        <tr>
                          <td className="text-center p-6" colSpan={8}>
                            <CircularProgress />
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td
                            className="text-center text-slate-300 font-semibold text-lg lg:text-2xl p-6"
                            colSpan={8}
                          >
                            Tidak ada data yang ditampilkan
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <PaginationComponent
                offset={offset}
                setOffset={setOffset}
                limit={limit}
                setLimit={setLimit}
                total={data && data.count}
              />
            </div>
          </div>
        </div>
      </div>

      <ModalDateRange
        open={openCalendar}
        setOpen={setOpenCalendar}
        selectedDate={selectedDates}
        setSelectedDate={setSelectedDates}
      />
    </div>
  );
};

export default HistoryDoctorPage;
